import api from './api.service';

export type TrailDomain = 'procurement' | 'sales';

export interface InheritedDocument {
  id: string;
  file_name: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  document_type: string | null;
  document_category: string | null;
  created_at: string | null;
  /** Extra context, e.g. "MTC · H-123" or "Line 3". */
  tag?: string | null;
}

export interface InheritedAttachmentGroup {
  source_type: string;
  source_id: string;
  source_number: string | null;
  source_status: string | null;
  documents: InheritedDocument[];
}

interface SourceMeta {
  label: string;
  path?: (id: string) => string;
}

const PROCUREMENT_SOURCES: Record<string, SourceMeta> = {
  PurchaseRequisition: { label: 'Purchase Request', path: (id) => `/scm/procurement/purchase-requests/${id}` },
  SupplierInquiry: { label: 'Supplier Inquiry', path: (id) => `/scm/procurement/supplier-inquiries/${id}` },
  SupplierQuotation: { label: 'Supplier Quotation', path: (id) => `/scm/procurement/supplier-quotations/${id}` },
  BidAnalysis: { label: 'Bid Analysis', path: (id) => `/scm/procurement/bid-analyses/${id}` },
  SpotPurchaseOffer: { label: 'Spot Purchase Offer' },
  PurchaseOrder: { label: 'Purchase Order', path: (id) => `/scm/procurement/purchase-orders/${id}` },
  GoodsReceiptNote: { label: 'GRN', path: (id) => `/scm/procurement/grn/${id}` },
  SupplierInvoice: { label: 'Supplier Invoice', path: (id) => `/scm/procurement/supplier-invoices/${id}` },
  PurchaseReturn: { label: 'Purchase Return', path: (id) => `/scm/procurement/purchase-returns/${id}` },
  SupplierAdvanceRequest: {
    label: 'Advance Request',
    path: (id) => `/scm/procurement/supplier-advance-requests/${id}`,
  },
  PaymentVoucher: { label: 'Payment Voucher', path: (id) => `/fin/payment-vouchers/${id}` },
};

const SALES_SOURCES: Record<string, SourceMeta> = {
  Lead: { label: 'Lead', path: (id) => `/growth/leads/${id}` },
  Opportunity: { label: 'Opportunity', path: (id) => `/growth/opportunities/${id}` },
  Estimation: { label: 'Estimation', path: (id) => `/growth/estimations/multi-item/${id}` },
  TradingRentalEstimation: {
    label: 'Trading / Rental Estimation',
    path: (id) => `/growth/estimations/trading-rental/${id}`,
  },
  BOQ: { label: 'BOQ', path: (id) => `/growth/estimations/boq/edit/${id}` },
  OrderReviewChecklist: { label: 'Order Review Checklist', path: (id) => `/growth/order-review-checklists/${id}` },
  Quotation: { label: 'Quotation', path: (id) => `/growth/quotations/${id}` },
  ContractReview: { label: 'Contract Review', path: (id) => `/growth/contract-reviews/${id}` },
  ProductionRequest: { label: 'Production Request', path: (id) => `/mfg/production-requests/${id}` },
  SalesOrder: { label: 'Customer Order', path: (id) => `/growth/orders/${id}` },
  DeliveryNote: { label: 'Delivery Note', path: (id) => `/growth/delivery-notes/${id}` },
  SalesInvoice: { label: 'Tax Invoice', path: (id) => `/growth/invoices/${id}` },
  ReceiptVoucher: { label: 'Receipt Voucher', path: (id) => `/fin/receipt-vouchers/${id}` },
  CustomerCreditNote: { label: 'Credit Note', path: (id) => `/fin/credit-notes/${id}` },
};

const SOURCES: Record<TrailDomain, Record<string, SourceMeta>> = {
  procurement: PROCUREMENT_SOURCES,
  sales: SALES_SOURCES,
};

/** Chain start documents have nothing upstream to inherit from. */
const CHAIN_STARTS = new Set(['PurchaseRequisition', 'Lead']);

/** Documents that can inherit attachments from upstream sources in the given domain. */
export function isTrailEntityType(domain: TrailDomain, type: string): boolean {
  return type in SOURCES[domain] && !CHAIN_STARTS.has(type);
}

export function trailSourceLabel(domain: TrailDomain, type: string): string {
  return SOURCES[domain][type]?.label ?? type;
}

export function trailSourcePath(domain: TrailDomain, type: string, id: string): string | undefined {
  return SOURCES[domain][type]?.path?.(id);
}

export const documentTrailService = {
  async getInheritedAttachments(
    domain: TrailDomain,
    entityType: string,
    entityId: string
  ): Promise<InheritedAttachmentGroup[]> {
    const res = await api.get(
      `/${domain}/document-trail/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/attachments`
    );
    const data = res?.data ?? res;
    const list = data?.data ?? data;
    return Array.isArray(list) ? list : [];
  },
};
